import { useCallback, useMemo } from "react"
import type { MouseEvent } from "react"

import { DataTable } from "@/components/ui/data-table/data-table"
import { SidebarInset } from "@/components/ui/sidebar"
import { Spinner } from "@/components/ui/spinner"
import { AIParserEmptyState } from "@/features/ai-parser/components/ai-parser-empty-state"
import { AIParserHeader } from "@/features/ai-parser/components/ai-parser-header"
import { UploadDragOverlay } from "@/features/ai-parser/components/upload-drag-overlay"
import { useAIParserSession } from "@/features/ai-parser/hooks/use-ai-parser-session"
import { useRunParserMutation } from "@/features/ai-parser/hooks/use-run-parser-mutation"
import { useTableTransformations } from "@/features/ai-parser/hooks/use-table-transformations"
import { useUploadZone } from "@/features/ai-parser/hooks/use-upload-zone"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { useAIParserStore } from "@/stores/ai-parser-store"
import { useParserSelectionStore } from "@/stores/parser-selection-store"

export function AIParserPage() {
  const { tableColumns, tableData, files, columns, getFullSessionQuery } = useTableTransformations()
  const { currentSessionId, sessionType } = useAIParserSession()
  const parserStore = useAIParserStore(sessionType)

  // Sync URL sessionId → ChatService at page level. Below the 1024px mobile
  // breakpoint the sidebar (and its ChatHistory) is an unmounted Sheet (#3515).
  useChatHistory({ sessionType, useChatStore: () => parserStore })

  const { runParserMutation, isRunParserPending } = useRunParserMutation()
  const {
    dropzoneProps: { getRootProps, isDragActive },
    isUploading,
  } = useUploadZone()

  const {
    selectMode,
    selectedFileIndices,
    selectedColumnIds,
    toggleFileSelection,
    toggleColumnSelection,
    selectFileRange,
    lastSelectedFileIndex,
  } = useParserSelectionStore()

  const handleRowClick = useCallback(
    (rowIndex: number, event: MouseEvent) => {
      const row = tableData[rowIndex]
      if (!row.documentName) return

      if (event.shiftKey && lastSelectedFileIndex !== null) {
        selectFileRange(lastSelectedFileIndex, rowIndex)
      } else {
        toggleFileSelection(rowIndex)
      }
    },
    [tableData, lastSelectedFileIndex, selectFileRange, toggleFileSelection]
  )

  const handleHeaderClick = useCallback(
    (columnId: string) => {
      toggleColumnSelection(columnId)
    },
    [toggleColumnSelection]
  )

  const isRowSelected = useCallback(
    (rowIndex: number) => selectedFileIndices.has(rowIndex),
    [selectedFileIndices]
  )

  const isHeaderSelected = useCallback(
    (columnId: string) => selectedColumnIds.has(columnId),
    [selectedColumnIds]
  )

  const hasFiles = files.length > 0
  const hasColumns = columns.length > 0
  const hasAnyExtractedValues = useMemo(
    () => files.some((file) => Object.keys(file.extracted_values ?? {}).length > 0),
    [files]
  )

  const isLoadingExistingSession = !!currentSessionId && getFullSessionQuery.isPending

  const isEmptySession = !hasFiles || (!hasColumns && !hasAnyExtractedValues)
  const showEmptyState = !isLoadingExistingSession && isEmptySession

  const handleRunParser = useCallback(() => {
    if (!currentSessionId) return
    runParserMutation.mutate({ sessionId: currentSessionId, background: true })
  }, [currentSessionId, runParserMutation])

  const renderContent = () => {
    if (isLoadingExistingSession) {
      return (
        <div className="flex h-full items-center justify-center">
          <Spinner />
        </div>
      )
    }

    if (showEmptyState) {
      return (
        <AIParserEmptyState
          hasColumns={hasColumns}
          hasFiles={hasFiles}
          isRunDisabled={isRunParserPending || !hasFiles || !hasColumns}
          onRunParser={handleRunParser}
        />
      )
    }

    return (
      <DataTable
        enableColumnResizing
        className="flex h-full flex-col rounded-none border-none"
        columns={tableColumns}
        data={tableData}
        isHeaderSelected={isHeaderSelected}
        isRowSelected={isRowSelected}
        selectMode={selectMode}
        tableClassName="h-full"
        onHeaderClick={handleHeaderClick}
        onRowClick={handleRowClick}
      />
    )
  }

  return (
    <SidebarInset className="module-enter relative h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] !overflow-hidden">
      <AIParserHeader />
      <div
        className="bg-background relative flex h-full flex-col overflow-x-auto"
        {...getRootProps()}
      >
        {renderContent()}
        <UploadDragOverlay isDragOver={isDragActive} isUploading={isUploading} />
      </div>
    </SidebarInset>
  )
}
